import { ApiError } from "@/api";
import i18n from "@/i18n";

import { countdownParts, secondsUntil } from "./cue-cooldown-format";
import { formatRetryCountdown } from "./retry-countdown";

/**
 * The refusal a sender gets when they tap Cue on someone they are still braked
 * against — after withdrawing a Cue on them, or after one ran out unanswered.
 *
 * The person is still standing in the Nearby list. That is deliberate: the
 * brake paces repeat sending, it does not remove someone from the room. But it
 * means this message is the *only* thing telling the sender why the tap did
 * nothing, so it has to be specific, and the countdown has to be right.
 *
 * The server sends its own English sentence as `message`. We re-render it here
 * instead of printing it, because the sentence was true when the response was
 * built and the user reads it after — and because it arrives in English
 * whatever language the app is in.
 */

export { formatRetryCountdown };

export type CueCooldownCause = "cancelled" | "expired";

export type CueCooldownRefusal = {
  code: "CUE_COOLDOWN";
  cause: CueCooldownCause;
  retryAt: string;
  retryAfterSeconds: number;
  message: string;
};

/** The refusal body, or null if this failure was something else entirely. */
export function cueCooldownFrom(error: unknown): CueCooldownRefusal | null {
  if (!(error instanceof ApiError)) return null;
  const body = error.body as Partial<CueCooldownRefusal> | undefined;
  if (!body || body.code !== "CUE_COOLDOWN") return null;
  if (typeof body.retryAt !== "string") return null;
  return {
    code: "CUE_COOLDOWN",
    cause: body.cause === "cancelled" ? "cancelled" : "expired",
    retryAt: body.retryAt,
    retryAfterSeconds:
      typeof body.retryAfterSeconds === "number" ? body.retryAfterSeconds : 0,
    message: typeof body.message === "string" ? body.message : "",
  };
}

/**
 * The sentence to show, counted from *now* rather than from when the response
 * was built — a prompt the user opens a minute later should not still claim the
 * original figure. A `retryAt` that will not parse falls back to the figure the
 * server sent, so a malformed date degrades to "slightly stale" rather than
 * "one second".
 */
export function cueCooldownMessage(
  refusal: CueCooldownRefusal,
  now: Date = new Date(),
): string {
  const remaining = secondsUntil(refusal.retryAt, now);
  const countdown = formatRetryCountdown(remaining ?? refusal.retryAfterSeconds);

  return i18n.t(
    refusal.cause === "cancelled"
      ? "social.cues.cooldownCancelled"
      : "social.cues.cooldownExpired",
    { countdown },
  );
}
